import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check } from "lucide-react";
import GlassCard from "../components/GlassCard";
import api from "../lib/api";
import PageShell from "../components/PageShell";

const stepsList = [
  { key: "transcribing", label: "Transcribing" },
  { key: "summarizing", label: "Summarizing" },
  { key: "preparing", label: "Preparing for review" },
];

const Processing = () => {
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [done, setDone] = useState({});
  const [error, setError] = useState("");
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    const pending = window._wellPendingAudio;
    if (!pending) {
      nav("/dashboard", { replace: true });
      return;
    }
    setTimeout(() => setStepIdx(1), 1500);
    setTimeout(() => setStepIdx(2), 4000);

    (async () => {
      try {
        const blob = new Blob([pending.buffer], { type: pending.type });
        const ext = (pending.type || "").includes("mp4") || (pending.type || "").includes("aac") ? "mp4" : "webm";
        const fd = new FormData();
        fd.append("audio", blob, `session.${ext}`);
        fd.append("client_name", pending.clientName || "Client");
        const r = await api.post("/sessions/process", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 240000,
        });
        setDone({ transcribing: true, summarizing: true, preparing: true });
        delete window._wellPendingAudio;
        setTimeout(() => {
          nav(`/review/${r.data.session_id}`, { replace: true });
        }, 600);
      } catch (e) {
        setError(e?.response?.data?.detail || "Processing failed");
      }
    })();
  }, [nav]);

  useEffect(() => {
    if (stepIdx >= 1) setDone((d) => ({ ...d, transcribing: true }));
    if (stepIdx >= 2) setDone((d) => ({ ...d, summarizing: true }));
  }, [stepIdx]);

  return (
    <PageShell>
      <div className="min-h-screen flex flex-col items-center justify-center max-w-md mx-auto py-12 gap-8 well-page-enter">
        <h1 className="text-3xl font-bold text-[#2A3B32]">Processing</h1>
        <GlassCard className="w-full">
          <div className="flex flex-col gap-6">
            {stepsList.map((s, i) => {
              const isDone = done[s.key];
              const isActive = !isDone && i === stepIdx;
              const visible = i <= stepIdx;
              return (
                <div
                  key={s.key}
                  data-testid={`step-${s.key}`}
                  className={`flex items-center gap-4 ${visible ? "well-fade-in" : "opacity-0"}`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-9 h-9 rounded-full bg-white/60 border border-white/50 flex items-center justify-center">
                    {isDone ? (
                      <Check size={18} className="text-[#6A7F6C]" strokeWidth={2.5} />
                    ) : isActive ? (
                      <Loader2 size={18} className="text-[#8BA38E] animate-spin" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-[#8A9C91]/40" />
                    )}
                  </div>
                  <p className={`text-base ${isDone || isActive ? "text-[#2A3B32]" : "text-[#8A9C91]"}`}>
                    {s.label}
                  </p>
                </div>
              );
            })}
          </div>
        </GlassCard>
        {error && (
          <GlassCard className="w-full text-center">
            <p className="text-[#B85C5C]" data-testid="processing-error">{error}</p>
            <button onClick={() => nav("/dashboard")} className="mt-3 text-[#8BA38E] underline">
              Back to dashboard
            </button>
          </GlassCard>
        )}
      </div>
    </PageShell>
  );
};

export default Processing;
